import { useCallback, useMemo, useRef, useState } from 'react';
import { useTheme } from '@shared/components/ui/theme-provider';
import { useQuery } from '@tanstack/react-query';
import {
  OrdersExternalApiAdapter,
  type ExternalOrderApi,
  isImportedExternalOrder,
} from '../adapters/OrdersExternalApiAdapter';

/**
 * Shopify trata `since_id` como exclusivo (solo pedidos con id > since_id).
 * Para que el pedido elegido entre en la importación hay que enviar el id del
 * pedido inmediatamente anterior en la secuencia de Shopify (el mayor id < seleccionado).
 */
function getSinceIdForManualImport(
  selectedExternalId: string,
  allOrders: ExternalOrderApi[]
): string {
  try {
    const selected = BigInt(String(selectedExternalId));
    let prev: bigint | null = null;
    for (const o of allOrders) {
      const id = BigInt(String(o.external_id));
      if (id < selected && (prev === null || id > prev)) {
        prev = id;
      }
    }
    if (prev !== null) {
      return String(prev);
    }
  } catch {
    const selectedStr = String(selectedExternalId);
    let best: string | null = null;
    for (const o of allOrders) {
      const idStr = String(o.external_id);
      if (idStr < selectedStr && (best === null || idStr > best)) {
        best = idStr;
      }
    }
    if (best !== null) {
      return best;
    }
  }
  return '0';
}
import { OrdersImportApiAdapter } from '../adapters/OrdersImportApiAdapter';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

// Función de utilidad para generar acrónimos
const generateAcronym = (name: string): string => {
  if (!name) return '';
  return name
    .split(' ')
    .slice(0, 3) // Tomar solo las primeras 3 palabras
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

// ========================================
// TIPOS Y INTERFACES
// ========================================

export interface SearchResult {
  id: string; // ID de la orden externa
  count: number; // Cantidad de ítems
  imported: boolean;
}

export interface OrderItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  image: string; // Usar imagen de API si existe, si no, example.webp
  acronym: string;
}

export interface OrderDetails {
  id: string; // ID de la orden externa (ref)
  external_id: string; // ID externo real para la API
  items: OrderItem[];
}

export interface UseImportOrderReturn {
  // Estados
  searchQuery: string;
  /** Lista a mostrar: todos los pedidos cargados o filtrados por `searchQuery` */
  searchResults: SearchResult[];
  selectedOrder: OrderDetails | null;
  emptyStateImage: string;
  isLoading: boolean;
  isImporting: boolean;
  noOrdersFound: boolean;

  // Acciones
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOrderSelect: (orderId: string) => void;
  handleBackToSearch: () => void;
  resetState: () => void;
  loadExternalOrdersOnce: (options?: { force?: boolean }) => void | Promise<void>;
  handleImportOrder: () => void;
}

// ========================================
// HOOK UNIFICADO PARA IMPORTACIÓN DE ÓRDENES
// ========================================

export const useImportOrder = (params?: { shopId?: number; channelId?: number }): UseImportOrderReturn => {
  const { theme } = useTheme();
  const { toast } = useToast();
  const { t } = useTranslation('shopsModule');
  const queryClient = useQueryClient();
  const shopId = params?.shopId;
  const channelId = params?.channelId;

  // ========================================
  // ESTADOS
  // ========================================

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<OrderDetails | null>(null);
  const [allExternalOrders, setAllExternalOrders] = useState<ExternalOrderApi[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [noOrdersFound, setNoOrdersFound] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const triggerLoadRef = useRef(false);

  // ========================================
  // CONFIGURACIÓN Y DATOS MOCK
  // ========================================

  // Imagen de estado vacío adaptativa según el tema
  const emptyStateImage =
    theme === 'dark'
      ? '/imagen/empty-tables/empty-product-stock-dark.svg'
      : '/imagen/empty-tables/empty-product-stock-light.svg';

  // Adapter de órdenes externas
  const externalAdapterRef = useRef(new OrdersExternalApiAdapter());
  const importAdapterRef = useRef(new OrdersImportApiAdapter());

  // Consulta React Query, activada bajo demanda una sola vez por tienda/canal
  const { refetch: refetchExternal, isFetching: isLoading } = useQuery({
    queryKey: ['externalOrders', shopId, channelId],
    queryFn: async () => {
      const data = await externalAdapterRef.current.getExternalOrders({
        shopId: Number(shopId),
        channelId: Number(channelId),
      });
      return data;
    },
    enabled: false, // Se dispara manualmente
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const loadExternalOrdersOnce = useCallback(
    async (options?: { force?: boolean }) => {
      const force = Boolean(options?.force);
      if (!shopId || !channelId) return;
      if (!force && (hasLoaded || triggerLoadRef.current)) return;
      if (force) {
        triggerLoadRef.current = false;
        setAllExternalOrders([]);
        setHasLoaded(false);
        setNoOrdersFound(false);
        await queryClient.invalidateQueries({
          queryKey: ['externalOrders', shopId, channelId],
        });
      }
      if (triggerLoadRef.current) return;
      triggerLoadRef.current = true;
      try {
        const result = await refetchExternal();
        if ((result as any)?.error) {
          const apiMessage =
            (result as any)?.error?.response?.data?.message ||
            (result as any)?.error?.message ||
            t('toasts.error.unexpected');
          toast({
            title: t('toasts.error.title'),
            description: String(apiMessage),
            variant: 'destructive',
          });
          // Permitir reintento si falla
          triggerLoadRef.current = false;
          setNoOrdersFound(true);
          return;
        }
        const data = result.data ?? [];
        setAllExternalOrders(data as ExternalOrderApi[]);
        setHasLoaded(true);
        // Si no hay datos, marcar el estado correspondiente
        if (data.length === 0) {
          setNoOrdersFound(true);
        }
      } catch (error: any) {
        const apiMessage = error?.response?.data?.message || error?.message || t('toasts.error.unexpected');
        toast({
          title: t('toasts.error.title'),
          description: String(apiMessage),
          variant: 'destructive',
        });
        triggerLoadRef.current = false;
        setNoOrdersFound(true);
      }
    },
    [shopId, channelId, hasLoaded, queryClient, refetchExternal, t, toast],
  );

  const searchResults = useMemo((): SearchResult[] => {
    if (!allExternalOrders.length) return [];
    const q = searchQuery.trim().toLowerCase();
    const source = q
      ? allExternalOrders.filter((order) => String(order.ref).toLowerCase().includes(q))
      : allExternalOrders;
    return source.map((order) => ({
      id: String(order.ref),
      count: order.lines?.length ?? 0,
      imported: isImportedExternalOrder(order),
    }));
  }, [allExternalOrders, searchQuery]);

  // ========================================
  // MANEJADORES DE EVENTOS
  // ========================================

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Maneja la selección de una orden de la lista
  const handleOrderSelect = (orderRef: string) => {
    const found = allExternalOrders.find((o) => String(o.ref) === String(orderRef));
    if (!found || isImportedExternalOrder(found)) return;
    const items: OrderItem[] = (found.lines ?? []).map((it) => ({
      id: String(it.product_id),
      name: it.name,
      sku: it.sku || String(it.variant_id) || 'N/A',
      quantity: Number(it.qty) || 0,
      image: (it.image && String(it.image)) || '', // Dejar vacío si no hay imagen
      acronym: generateAcronym(it.name),
    }));
    const orderDetails: OrderDetails = {
      id: String(found.ref),
      external_id: String(found.external_id),
      items,
    };
    setSelectedOrder(orderDetails);
  };

  // Maneja el regreso a la vista de búsqueda
  const handleBackToSearch = () => {
    setSelectedOrder(null);
    setSearchQuery('');
  };

  // Resetea todo el estado (útil para cuando se abre el modal)
  const resetState = () => {
    setSearchQuery('');
    setSelectedOrder(null);
    setAllExternalOrders([]);
    setHasLoaded(false);
    setNoOrdersFound(false);
    triggerLoadRef.current = false;
  };

  // Maneja la acción de importar la orden seleccionada
  const handleImportOrder = useCallback(async () => {
    if (!selectedOrder || !shopId) return;

    setIsImporting(true);
    try {
      const sinceId = getSinceIdForManualImport(
        selectedOrder.external_id,
        allExternalOrders
      );

      const response = await importAdapterRef.current.importManualOrder({
        shop_id: shopId,
        since_id: sinceId,
      });

      if (response.success) {
        toast({
          title: t('modals.importOrder.toast.success.title'),
          description: response.message || t('modals.importOrder.toast.success.description'),
          variant: 'default',
        });
        // Invalidar query de órdenes para refrescar la lista principal
        await queryClient.invalidateQueries({ queryKey: ['orders'] });
        // Opcional: resetear y volver a la búsqueda
        handleBackToSearch();
      } else {
        toast({
          title: t('modals.importOrder.toast.error.title'),
          description: response.message || t('toasts.error.unexpected'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.message || t('toasts.error.unexpected');
      toast({
        title: t('modals.importOrder.toast.error.title'),
        description: String(apiMessage),
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  }, [selectedOrder, shopId, allExternalOrders, t, toast, queryClient]);

  return {
    // Estados
    searchQuery,
    searchResults,
    selectedOrder,
    emptyStateImage,
    isLoading,
    isImporting,
    noOrdersFound,

    // Acciones
    handleSearchChange,
    handleOrderSelect,
    handleBackToSearch,
    resetState,
    loadExternalOrdersOnce,
    handleImportOrder,
  };
};
